import { applySnapshot, cast, flow, getEnv, types } from "mobx-state-tree";
import RequestFlowStore from "./jobs/requestFlow";
import { transformInput } from "./jobs/utils/misc";
import { RootStore } from "./rootStore";

const PaymentDetails = types.model("PaymentDetails", {
  tuitionFee: types.optional(types.number, 0),
  daysSelected: types.array(types.string),
  totalLessons: types.optional(types.number, 0),
  totalTuition: types.optional(types.number, 0),
  daysRequested: types.array(types.string),
  hoursOfLesson: types.optional(types.number, 0),
  totalDiscount: types.optional(types.number, 0),
  transportFare: types.optional(types.number, 0),
  activeTeacherIDs: types.array(types.string),
  isBillableDistance: types.optional(types.boolean, false),
  // lessonPayments: [],
});

const ExtraData = types.model("ExtraData", {
  payment: types.optional(PaymentDetails, {}),
});

const RequestStore = RequestFlowStore.named("RequestStore")
  .views((self) => {
    return {
      get childDetails() {
        return self.childStore.fullData.childDetails;
      },
      get subjects() {
        return this.childDetails
          .map((childDetail: any) => childDetail?.classDetail?.subjects)
          .flat();
      },
      get lessonLocation() {
        return [
          self.lessonLocationStore.vicinity,
          self.lessonLocationStore.region,
          self.lessonLocationStore.state,
        ]
          .filter((data) => data)
          .join(", ");
      },
      get tutorGender() {
        return self.filterStore.gender;
      },
      get jobHeader() {
        return `${
          this.tutorGender.length > 1 ? "" : this.tutorGender[0] + " "
        }${
          self.lessonLocationStore.lessonType === "physical" ? "Home" + " " : ""
        }Tutor in ${this.lessonLocation}`;
      },
      get classes() {
        return this.childDetails
          .map((childDetail: any) => childDetail?.classDetail?.class)
          .flat()
          .join(", ");
      },
      get curriculum() {
        return this.childDetails
          .map((childDetail: any) => childDetail?.classDetail?.subjects)
          .flat()
          .join(", ");
      },

      get isJobOpen() {
        // implementation goes here
        return true;
      },
    };
  })
  .actions((self) => {
    const initializeStore = (data: any) => {
      let {
        contactDetails = {},
        lessonDetails = {},
        childDetails = [],
        filters = {},
        slug = "",
        tutor_info = {},
        status,
        created,
        agent = {},
        tutorResponse = {},
        paymentInfo = {},
      } = data;
      const formattedData = {
        lessonLocationStore: {
          lessonType: lessonDetails.lessonType,
          country: contactDetails.country,
          state: contactDetails.state,
          region: contactDetails.region,
          address: contactDetails.address,
          vicinity: contactDetails.vicinity,
        },
        lessonScheduleStore: {
          ...lessonDetails.lessonSchedule,
        },
        teacherSelectStore: {
          teacherOption: data.teacherKind,
        },
        childStore: {
          childDetails: transformInput(childDetails).childDetails,
        },
        contactInfoStore: contactDetails,
        filterStore: filters,
        slug,
        serverInfo: {
          created,
          status,
          agent,
          tutorResponseInfo: tutorResponse,
          tutorRequestInfo: {
            ...tutor_info,
            tutorData: paymentInfo?.tutor?.tutor || paymentInfo.tutor,
            rawPaymentInfo: paymentInfo?.tutor?.tutor
              ? paymentInfo.tutor
              : paymentInfo,
          },
        },
        payment: paymentInfo,
      };
      applySnapshot(self, formattedData);
    };
    return {
      initializeStore,
    };
  });

export const ExtendedRequestStore: any = types
  .compose(RequestStore, ExtraData)
  .named("ExtendedRequestStore");

const FilterStore = types
  .model("FlterStore", {
    subject: types.optional(types.string, ""),
    vicinity: types.optional(types.string, ""),
    state: types.optional(types.string, ""),
    gender: types.optional(types.string, ""),
    latitude: types.optional(types.number, 0),
    longitude: types.optional(types.number, 0),
  })
  .views((self) => ({
    get initialValues() {
      return {
        subject: self.subject,
        vicinity: self.vicinity,
        state: self.state,
        gender: self.gender,
      };
    },
  }))
  .actions((self) => ({
    initializeFilter(data: any) {
      applySnapshot(self, data);
    },
  }));

export const TutoringJobStore: any = types
  .model("TutorJobList", {
    tutorInfo: types.maybeNull(RootStore),
    jobs: types.optional(types.array(ExtendedRequestStore), []),
    isLoggedIn: types.optional(types.boolean, false),
    filter: types.optional(FilterStore, {}),
    loading: types.optional(types.boolean, false),
  })
  .actions((self) => {
    const { adapter } = getEnv(self);
    function bulkMapToStore(jobs: any) {
      let requests = jobs.map((data: any) => {
        let {
          contactDetails = {},
          lessonDetails = {},
          childDetails = [],
          filters = {},
          slug = "",
          tutor_info = {},
          status,
          created,
          agent = {},
          tutorResponse = {},
          paymentInfo = {},
        } = data;
        return {
          lessonLocationStore: {
            lessonType: lessonDetails.lessonType,
            country: contactDetails.country,
            state: contactDetails.state,
            region: contactDetails.region,
            address: contactDetails.address,
            vicinity: contactDetails.vicinity,
          },
          lessonScheduleStore: {
            ...lessonDetails.lessonSchedule,
          },
          teacherSelectStore: {
            teacherOption: data.teacherKind,
          },
          childStore: {
            childDetails: transformInput(childDetails).childDetails,
          },
          contactInfoStore: contactDetails,
          filterStore: filters,
          slug,
          serverInfo: {
            created,
            status,
            agent,
            tutorResponseInfo: tutorResponse,
            tutorRequestInfo: {
              ...tutor_info,
              tutorData: paymentInfo?.tutor?.tutor || paymentInfo.tutor,
              rawPaymentInfo: paymentInfo?.tutor?.tutor
                ? paymentInfo.tutor
                : paymentInfo,
            },
          },
          payment: paymentInfo,
        };
      });
      self.jobs = cast(requests);
    }
    const initializeStore = flow(function* initializeStore() {
      try {
        let jobs = yield adapter.fetchTutoringJobs();
        bulkMapToStore(jobs);
      } catch (error) {
        throw error;
      }
    });
    const onSearch = (values: any) => {};
    const onLogout = () => {};
    const onLogin = flow(function* onLogin(values: any) {
      return {};
    });
    const onFormSubmit = flow(function* onFormSubmit(values: any) {
      return {};
    });

    return {
      initializeStore,
      onSearch,
      onLogout,
      onLogin,
      onFormSubmit,
    };
  });
